import React from 'react'
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon
} from '@ionic/react'
import { arrowBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import PlatformService from '../../services/platform'

const GDPRCompliance: React.FC = () => {
  const { t, i18n } = useTranslation()
  const isGerman = i18n.language === 'de'
  const isWeb = PlatformService.isWeb()

  return (
    <IonPage>
      <PageHeader title={t('legal.gdprCompliance')} />
      <IonContent className="ion-padding">
        <div className="max-w-4xl mx-auto">
          {isWeb && (
            <IonButton
              fill="clear"
              routerLink="/"
              className="mb-4 hidden md:flex"
            >
              <IonIcon icon={arrowBackOutline} slot="start" />
              {t('legal.backToHome')}
            </IonButton>
          )}

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('legal.gdprCompliance')}</IonCardTitle>
              <p className="text-sm text-gray-600">
                {t('gdpr.lastUpdated')}: 2025-06-01
              </p>
            </IonCardHeader>
                         <IonCardContent>
               <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
            <div className="mb-8">
              <p className="text-gray-600 mb-4">
                {t('gdpr.lastUpdated')}: 2025-06-01
              </p>
              
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.introduction.title')}</h2>
              <p className="mb-4">
                {t('gdpr.introduction.content')}
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.dataController.title')}</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>InsightFlow</strong></p>
                <p>Email: contact@insightflow.app</p>
                <p>Address: [Company Address]</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.legalBasis.title')}</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>{t('gdpr.legalBasis.consent')}</li>
                <li>{t('gdpr.legalBasis.contract')}</li>
                <li>{t('gdpr.legalBasis.legitimateInterest')}</li>
                <li>{t('gdpr.legalBasis.legal')}</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.dataTypes.title')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">{t('gdpr.dataTypes.personal.title')}</h3>
                  <ul className="text-sm space-y-1">
                    <li>• {t('gdpr.dataTypes.personal.name')}</li>
                    <li>• {t('gdpr.dataTypes.personal.email')}</li>
                    <li>• {t('gdpr.dataTypes.personal.profile')}</li>
                  </ul>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">{t('gdpr.dataTypes.usage.title')}</h3>
                  <ul className="text-sm space-y-1">
                    <li>• {t('gdpr.dataTypes.usage.sessions')}</li>
                    <li>• {t('gdpr.dataTypes.usage.interactions')}</li>
                    <li>• {t('gdpr.dataTypes.usage.preferences')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.rights.title')}</h2>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold">{t('gdpr.rights.access.title')}</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.rights.access.description')}</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold">{t('gdpr.rights.rectification.title')}</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.rights.rectification.description')}</p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="font-semibold">{t('gdpr.rights.erasure.title')}</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.rights.erasure.description')}</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold">{t('gdpr.rights.portability.title')}</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.rights.portability.description')}</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold">{t('gdpr.rights.objection.title')}</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.rights.objection.description')}</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.dataRetention.title')}</h2>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p>{t('gdpr.dataRetention.content')}</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>{t('gdpr.dataRetention.account')}</li>
                  <li>{t('gdpr.dataRetention.sessions')}</li>
                  <li>{t('gdpr.dataRetention.analytics')}</li>
                </ul>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.dataSecurity.title')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">{t('gdpr.dataSecurity.technical.title')}</h3>
                  <ul className="text-sm space-y-1">
                    <li>• {t('gdpr.dataSecurity.technical.encryption')}</li>
                    <li>• {t('gdpr.dataSecurity.technical.access')}</li>
                    <li>• {t('gdpr.dataSecurity.technical.monitoring')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{t('gdpr.dataSecurity.organizational.title')}</h3>
                  <ul className="text-sm space-y-1">
                    <li>• {t('gdpr.dataSecurity.organizational.training')}</li>
                    <li>• {t('gdpr.dataSecurity.organizational.procedures')}</li>
                    <li>• {t('gdpr.dataSecurity.organizational.audits')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.thirdParties.title')}</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold">Supabase (Database)</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.thirdParties.supabase')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold">Vercel (Hosting)</h3>
                  <p className="text-sm text-gray-600">{t('gdpr.thirdParties.vercel')}</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.contact.title')}</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p>{t('gdpr.contact.content')}</p>
                <div className="mt-2">
                  <p><strong>Email:</strong> privacy@insightflow.app</p>
                  <p><strong>Response Time:</strong> {t('gdpr.contact.responseTime')}</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('gdpr.supervisory.title')}</h2>
              <p>{t('gdpr.supervisory.content')}</p>
            </div>

                            <div className="text-center text-sm text-gray-500 mt-8 pt-8 border-t">
                  <p>{t('gdpr.footer')}</p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default GDPRCompliance 